// src/merch/contour.js
//
// Auto dieline: a smooth contour cut line around whatever the customer
// designed (die-cut stickers, heat transfers). No fixed shape needed.
//
//   artwork alpha → fill holes (inside of O, A…) → grow by the offset
//   (1/8" by default) → "close" (round off notches, bridge pieces that are
//   close together) → trace the outline → simplify + smooth → mm rings
//
// Pure maths on an alpha grid (no DOM), so it runs the same in the editor
// (live preview), at checkout (print PDF) and in tests.

const INF = 1e20;

/* ---------- distance transform (Felzenszwalb & Huttenlocher) ---------- */

function edt1d(f, n, d, v, z) {
  let k = 0;
  v[0] = 0; z[0] = -INF; z[1] = INF;
  for (let q = 1; q < n; q++) {
    let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    while (s <= z[k]) { k--; s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]); }
    k++; v[k] = q; z[k] = s; z[k + 1] = INF;
  }
  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1] < q) k++;
    const dq = q - v[k];
    d[q] = dq * dq + f[v[k]];
  }
}

/** Squared distance from every cell to the nearest `on` cell. */
function sqDist(mask, w, h, on = 1) {
  const g = new Float64Array(w * h);
  for (let i = 0; i < g.length; i++) g[i] = mask[i] === on ? 0 : INF;
  const n = Math.max(w, h);
  const f = new Float64Array(n), d = new Float64Array(n), v = new Int32Array(n), z = new Float64Array(n + 1);
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) f[y] = g[y * w + x];
    edt1d(f, h, d, v, z);
    for (let y = 0; y < h; y++) g[y * w + x] = d[y];
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) f[x] = g[y * w + x];
    edt1d(f, w, d, v, z);
    for (let x = 0; x < w; x++) g[y * w + x] = d[x];
  }
  return g;
}

const dilate = (m, w, h, r) => { const d = sqDist(m, w, h, 1), o = new Uint8Array(w * h), r2 = r * r; for (let i = 0; i < o.length; i++) o[i] = d[i] <= r2 ? 1 : 0; return o; };
const erode = (m, w, h, r) => { const d = sqDist(m, w, h, 0), o = new Uint8Array(w * h), r2 = r * r; for (let i = 0; i < o.length; i++) o[i] = d[i] > r2 ? 1 : 0; return o; };

/* ---------- holes + pieces ---------- */

/** Fill enclosed holes: anything the outside can't reach becomes solid. */
function fillHoles(m, w, h) {
  const seen = new Uint8Array(w * h), st = [];
  const push = (i) => { if (!m[i] && !seen[i]) { seen[i] = 1; st.push(i); } };
  for (let x = 0; x < w; x++) { push(x); push((h - 1) * w + x); }
  for (let y = 0; y < h; y++) { push(y * w); push(y * w + w - 1); }
  while (st.length) {
    const i = st.pop(), x = i % w, y = (i / w) | 0;
    if (x > 0) push(i - 1); if (x < w - 1) push(i + 1);
    if (y > 0) push(i - w); if (y < h - 1) push(i + w);
  }
  const o = new Uint8Array(w * h);
  for (let i = 0; i < o.length; i++) o[i] = seen[i] ? 0 : 1;
  return o;
}

/** Label connected pieces → { labels, sizes[] } (4-connected). */
function pieces(m, w, h) {
  const lab = new Int32Array(w * h), sizes = [0];
  let n = 0;
  for (let s = 0; s < m.length; s++) {
    if (!m[s] || lab[s]) continue;
    n++; let size = 0;
    const st = [s]; lab[s] = n;
    while (st.length) {
      const i = st.pop(), x = i % w, y = (i / w) | 0; size++;
      const nb = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, y > 0 ? i - w : -1, y < h - 1 ? i + w : -1];
      for (const j of nb) if (j >= 0 && m[j] && !lab[j]) { lab[j] = n; st.push(j); }
    }
    sizes.push(size);
  }
  return { lab, sizes, count: n };
}

/* ---------- trace (marching squares) ---------- */

function trace(m, w, h) {
  // corners = cell centres; edge points at midpoints. Pad with 0 so every
  // outline closes.
  const at = (x, y) => (x < 0 || y < 0 || x >= w || y >= h ? 0 : m[y * w + x]);
  const segs = new Map(); // "x,y" start → [x2,y2] end (directed, solid on the left)
  const key = (x, y) => `${x},${y}`;
  const add = (a, b) => segs.set(key(a[0], a[1]), b);
  for (let y = -1; y < h; y++) {
    for (let x = -1; x < w; x++) {
      const tl = at(x, y), tr = at(x + 1, y), br = at(x + 1, y + 1), bl = at(x, y + 1);
      const c = (tl << 3) | (tr << 2) | (br << 1) | bl;
      if (c === 0 || c === 15) continue;
      // edge midpoints, stored shifted by +0.5 (subtracted when tracing)
      const top = [x + 1, y + 0.5], right = [x + 1.5, y + 1], bottom = [x + 1, y + 1.5], left = [x + 0.5, y + 1];
      switch (c) {
        case 1: add(left, bottom); break;
        case 2: add(bottom, right); break;
        case 3: add(left, right); break;
        case 4: add(right, top); break;
        case 5: add(right, top); add(left, bottom); break; // saddle: keep pieces apart
        case 6: add(bottom, top); break;
        case 7: add(left, top); break;
        case 8: add(top, left); break;
        case 9: add(top, bottom); break;
        case 10: add(top, left); add(bottom, right); break;
        case 11: add(top, right); break;
        case 12: add(right, left); break;
        case 13: add(right, bottom); break;
        case 14: add(bottom, left); break;
        default: break;
      }
    }
  }
  const rings = [];
  while (segs.size) {
    const [k0, first] = segs.entries().next().value;
    segs.delete(k0);
    const [sx, sy] = k0.split(",").map(Number);
    const ring = [[sx - 0.5, sy - 0.5]];
    let cur = first, guard = 0;
    while (guard++ < 1e6) {
      ring.push([cur[0] - 0.5, cur[1] - 0.5]);
      const k = key(cur[0], cur[1]);
      const nx = segs.get(k);
      if (!nx) break;
      segs.delete(k);
      cur = nx;
    }
    if (ring.length > 3) rings.push(ring);
  }
  return rings;
}

/* ---------- simplify + smooth ---------- */

function rdp(pts, eps) {
  if (pts.length < 3) return pts;
  const keep = new Uint8Array(pts.length); keep[0] = keep[pts.length - 1] = 1;
  const st = [[0, pts.length - 1]];
  while (st.length) {
    const [a, b] = st.pop();
    const [ax, ay] = pts[a], [bx, by] = pts[b];
    const dx = bx - ax, dy = by - ay, L = Math.hypot(dx, dy) || 1e-9;
    let best = -1, bi = -1;
    for (let i = a + 1; i < b; i++) {
      const d = Math.abs(dy * pts[i][0] - dx * pts[i][1] + bx * ay - by * ax) / L;
      if (d > best) { best = d; bi = i; }
    }
    if (best > eps) { keep[bi] = 1; st.push([a, bi], [bi, b]); }
  }
  return pts.filter((_, i) => keep[i]);
}
function simplifyRing(ring, eps) {
  // split the closed ring at its far point so RDP works on two open halves
  let far = 0, fd = -1;
  for (let i = 1; i < ring.length; i++) { const d = Math.hypot(ring[i][0] - ring[0][0], ring[i][1] - ring[0][1]); if (d > fd) { fd = d; far = i; } }
  const a = rdp(ring.slice(0, far + 1), eps), b = rdp(ring.slice(far).concat([ring[0]]), eps);
  return a.slice(0, -1).concat(b.slice(0, -1));
}
function chaikin(ring, iters = 2) {
  let p = ring;
  for (let it = 0; it < iters; it++) {
    const o = [];
    for (let i = 0; i < p.length; i++) {
      const a = p[i], b = p[(i + 1) % p.length];
      o.push([a[0] * 0.75 + b[0] * 0.25, a[1] * 0.75 + b[1] * 0.25], [a[0] * 0.25 + b[0] * 0.75, a[1] * 0.25 + b[1] * 0.75]);
    }
    p = o;
  }
  return p;
}
function convexHull(points) {
  const p = points.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lo = [], up = [];
  for (const q of p) { while (lo.length >= 2 && cross(lo[lo.length - 2], lo[lo.length - 1], q) <= 0) lo.pop(); lo.push(q); }
  for (let i = p.length - 1; i >= 0; i--) { const q = p[i]; while (up.length >= 2 && cross(up[up.length - 2], up[up.length - 1], q) <= 0) up.pop(); up.push(q); }
  return lo.slice(0, -1).concat(up.slice(0, -1));
}
const ringArea = (r) => { let a = 0; for (let i = 0; i < r.length; i++) { const p = r[i], q = r[(i + 1) % r.length]; a += p[0] * q[1] - q[0] * p[1]; } return a / 2; };

/**
 * Contour cut line for artwork.
 * @param alpha    Uint8 alpha per cell (row-major, w × h)
 * @param w,h      grid size in cells
 * @param pxPerMm  grid resolution
 * @param opts     offsetMm (default 3.175 = 1/8"), single (one shape
 *                 around everything, default true), minPieceMm2
 * @returns { rings: [[[xMm,yMm]…]…], warnings: string[] } — mm in the
 *          grid's own frame (cell (0,0) top-left corner = 0,0)
 */
export function contourFromAlpha(alpha, w, h, pxPerMm, opts = {}) {
  const offsetMm = opts.offsetMm ?? 3.175;
  const single = opts.single ?? true;
  const minPieceMm2 = opts.minPieceMm2 ?? 12;
  const warnings = [];

  // pad so the grown outline never touches the grid edge
  const smoothMm = offsetMm * (single ? 1 : 0.6);
  const pad = Math.ceil((offsetMm + smoothMm * 3 + 2) * pxPerMm);
  const W = w + pad * 2, H = h + pad * 2;
  let m = new Uint8Array(W * H), any = false;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (alpha[y * w + x] > 24) { m[(y + pad) * W + x + pad] = 1; any = true; }
  }
  if (!any) return { rings: [], warnings };

  m = fillHoles(m, W, H);
  const shape = (s) => {
    let g = dilate(m, W, H, (offsetMm + s) * pxPerMm);
    if (s > 0) g = erode(g, W, H, s * pxPerMm);
    return fillHoles(g, W, H);
  };
  let s = smoothMm, g = shape(s), pc = pieces(g, W, H);
  // one shape: bridge further until it's a single piece (a few tries)
  for (let t = 0; single && pc.count > 1 && t < 3; t++) {
    s *= 1.8; g = shape(s); pc = pieces(g, W, H);
  }

  // drop specks too small to cut
  const minCells = minPieceMm2 * pxPerMm * pxPerMm;
  let dropped = 0;
  for (let i = 0; i < g.length; i++) if (g[i] && pc.sizes[pc.lab[i]] < minCells) { g[i] = 0; }
  for (let k = 1; k <= pc.count; k++) if (pc.sizes[k] < minCells) dropped++;
  if (dropped) warnings.push(`${dropped} tiny piece${dropped > 1 ? "s" : ""} too small to cut ${dropped > 1 ? "were" : "was"} left off.`);

  const toMm = ([x, y]) => [(x - pad + 0.5) / pxPerMm, (y - pad + 0.5) / pxPerMm];
  let rings = trace(g, W, H)
    .map((r) => r.map(toMm))
    .filter((r) => Math.abs(ringArea(r)) >= minPieceMm2 * 0.5);
  // outer outlines only (holes were filled; this is just belt and braces)
  rings = rings.filter((r) => ringArea(r) !== 0);

  if (single && rings.length > 1) {
    // still apart after bridging: one smooth hull around everything
    rings = [convexHull(rings.flat())];
    warnings.push("Pieces are far apart, so they share one cut shape around everything.");
  }

  const step = 1 / pxPerMm;
  rings = rings.map((r) => simplifyRing(chaikin(simplifyRing(r, step * 0.35), 2), 0.04));
  return { rings, warnings };
}

/**
 * Contour from a canvas region (browser). `rect` = source px rect of the
 * trim box; `mm` = its size in mm. Works at ~`pxPerMm` (downsampled).
 */
export function contourFromCanvas(canvas, rect, mm, opts = {}) {
  const pxPerMm = opts.pxPerMm ?? 6;
  const w = Math.max(8, Math.round(mm.w * pxPerMm)), h = Math.max(8, Math.round(mm.h * pxPerMm));
  const c = document.createElement("canvas");
  c.width = w; c.height = h;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(canvas, rect.x, rect.y, rect.w, rect.h, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h).data;
  const a = new Uint8Array(w * h);
  for (let i = 0; i < a.length; i++) a[i] = d[i * 4 + 3];
  return contourFromAlpha(a, w, h, w / mm.w, opts);
}
